import AccountBoxIcon from '@mui/icons-material/AccountBox';
import BusinessIcon from '@mui/icons-material/Business';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { blue, blueGrey, grey } from '@mui/material/colors';
import type { MarginSummaryCategory } from 'api/managementReportApi';
import React from 'react';
import { formatMarginDetailDisplayName } from './marginSummaryLabels';
import {
  formatMarginPercentage,
  isDefaultCombination,
} from './marginSummaryTableUtils';

export type MarginSummaryMatrixTableProps = {
  category: MarginSummaryCategory;
  allCombinations: string[];
  paginatedNames: string[];
  totalItemCount: number;
  marginByNameAndCombination: Record<
    string,
    Record<string, number | null>
  >;
};

const MarginSummaryMatrixTable = ({
  category,
  allCombinations,
  paginatedNames,
  totalItemCount,
  marginByNameAndCombination,
}: MarginSummaryMatrixTableProps) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{ border: '1px solid #ccc', maxHeight: 560 }}>
    <Table stickyHeader size="medium">
      <TableHead>
        <TableRow>
          <TableCell
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 5,
              background: blueGrey[100],
              fontWeight: 'bold',
              minWidth: 220,
              borderRight: '1px solid #ccc',
            }}>
            Name
          </TableCell>
          {allCombinations.length > 0 ? (
            allCombinations.map((combination) => {
              const isDefault = isDefaultCombination(combination);
              return (
                <TableCell
                  key={`${category}-${combination}`}
                  align="center"
                  sx={{
                    background: isDefault ? blueGrey[50] : blue[50],
                    fontWeight: 'bold',
                    minWidth: 180,
                    borderRight: '1px solid #ccc',
                  }}>
                  {combination.split('/').map((part, index) => (
                    <Chip
                      key={`${combination}-${index}`}
                      label={part}
                      sx={{
                        margin: '2px',
                        color: '#000',
                        backgroundColor: isDefault ? grey[400] : blue[200],
                        paddingInline: '0.5rem',
                        paddingY: '1rem',
                      }}
                      size="small"
                      icon={
                        index === 0 ? (
                          <BusinessIcon
                            color="action"
                            sx={{
                              fontSize: '1rem',
                              color: '#000',
                            }}
                          />
                        ) : (
                          <AccountBoxIcon
                            color="action"
                            sx={{
                              fontSize: '1rem',
                              color: '#000',
                            }}
                          />
                        )
                      }
                    />
                  ))}
                </TableCell>
              );
            })
          ) : (
            <TableCell
              align="center"
              sx={{
                background: blueGrey[50],
                fontWeight: 'bold',
                borderRight: '1px solid #ccc',
              }}>
              No combinations available
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {totalItemCount > 0 ? (
          paginatedNames.map((name) => (
            <TableRow key={`${category}-${name}`} hover>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  background: '#fff',
                  minWidth: 220,
                  borderRight: '1px solid #ccc',
                }}>
                {formatMarginDetailDisplayName(category, name)}
              </TableCell>
              {allCombinations.length > 0 ? (
                allCombinations.map((combination) => {
                  const marginValue =
                    marginByNameAndCombination[name]?.[combination];
                  const isDefault = isDefaultCombination(combination);
                  return (
                    <TableCell
                      key={`${name}-${combination}`}
                      align="center"
                      sx={{
                        borderRight: '1px solid #ccc',
                        backgroundColor: isDefault ? grey[50] : undefined,
                      }}>
                      {marginValue === undefined ? (
                        '-'
                      ) : (
                        <Typography
                          color={
                            marginValue !== null && marginValue < 0
                              ? 'error'
                              : 'inherit'
                          }
                          variant="body2"
                          sx={{ fontWeight: 'bold' }}>
                          {formatMarginPercentage(marginValue)}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })
              ) : (
                <TableCell
                  align="center"
                  sx={{ borderRight: '1px solid #ccc' }}>
                  -
                </TableCell>
              )}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={Math.max(2, allCombinations.length + 1)}
              align="center">
              No detail rows available.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default MarginSummaryMatrixTable;
